// Paper the barcode sticker prints on (Stitches S8, flag product.barcodeSticker). Every size is in
// millimetres. A label's position is its trim box: the edge the label is cut or peeled at.

export type StickerPaperPresetKey =
  | 'labelRoll50x25'
  | 'a4CutSheet50x25'
  | 'a4FortyPerSheet52x29'
  | 'a4TwentyFourPerSheet70x37'
  | 'a4TwentyOnePerSheet63x38';

// roll: one label per page on a label printer. cutSheet: plain or full-sticker paper cut by hand or
// guillotine; labels sit edge to edge so straight cuts across the sheet separate them all. dieCut:
// ready-cut sticker sheets, printed edge to edge of the label with nothing outside it.
export type StickerPaperKind = 'roll' | 'cutSheet' | 'dieCut';

export type StickerPaperPreset = {
  key: StickerPaperPresetKey;
  label: string;
  kind: StickerPaperKind;
  pageWidth: number;
  pageHeight: number;
  labelWidth: number;
  labelHeight: number;
  columns: number;
  rows: number;
  // Top-left corner of the first label's trim box on the page.
  marginTop: number;
  marginLeft: number;
  // Distance from one label's trim corner to the next one's.
  pitchHorizontal: number;
  pitchVertical: number;
  // Artwork extends this far past the trim on every side. 0 on every preset today: the cut sheet's
  // labels share their edges, so there is nowhere for bleed to go between them.
  bleed: number;
  // Text and bars stay this far inside the trim.
  safeArea: number;
};

export const a4PageWidth = 210;
export const a4PageHeight = 297;

// Cut sheet: labels butt edge to edge, so every cut line is shared and straight cuts across the
// whole sheet separate them all; only the outer edge of the block is trimmed. Crop marks sit in the
// top and bottom margins at every vertical cut line. The side margins are too narrow for marks on
// most printers, so thin cut lines can also be printed along every cut line.
export const cropMarkLineWidth = 0.25;
export const cropMarkLength = 4;
export const cropMarkGapOutsideTrim = 1;
export const cutLineWidth = 0.1;

// Most printers cannot print the outermost 3–4 mm of a page.
const printerUnprintableMargin = 4;
// Side margin: nothing but the cut lines' ends is printed there.
const cutSheetMinimumSideMargin = 5;
// Top and bottom margin: room for the crop marks inside what the printer can print.
const cutSheetMinimumTopMargin = printerUnprintableMargin + cropMarkGapOutsideTrim + cropMarkLength;
const cutSheetLabelWidth = 50;
const cutSheetLabelHeight = 25;

// The most whole labels that fit edge to edge inside the margins, with the block centred.
export const computeCutSheetGrid = ({
  pageWidth,
  pageHeight,
  minimumSideMargin,
  minimumTopMargin,
  labelWidth,
  labelHeight,
}: {
  pageWidth: number;
  pageHeight: number;
  minimumSideMargin: number;
  minimumTopMargin: number;
  labelWidth: number;
  labelHeight: number;
}) => {
  const columns = Math.floor((pageWidth - 2 * minimumSideMargin) / labelWidth);
  const rows = Math.floor((pageHeight - 2 * minimumTopMargin) / labelHeight);
  return {
    columns,
    rows,
    marginLeft: roundMillimetres((pageWidth - columns * labelWidth) / 2),
    marginTop: roundMillimetres((pageHeight - rows * labelHeight) / 2),
    pitchHorizontal: labelWidth,
    pitchVertical: labelHeight,
  };
};

const roundMillimetres = (value: number) => Math.round(value * 100) / 100;

const cutSheetGrid = computeCutSheetGrid({
  pageWidth: a4PageWidth,
  pageHeight: a4PageHeight,
  minimumSideMargin: cutSheetMinimumSideMargin,
  minimumTopMargin: cutSheetMinimumTopMargin,
  labelWidth: cutSheetLabelWidth,
  labelHeight: cutSheetLabelHeight,
});

export const stickerPaperPresets: Record<StickerPaperPresetKey, StickerPaperPreset> = {
  labelRoll50x25: {
    key: 'labelRoll50x25',
    label: 'Label roll, 50 × 25 mm',
    kind: 'roll',
    pageWidth: 50,
    pageHeight: 25,
    labelWidth: 50,
    labelHeight: 25,
    columns: 1,
    rows: 1,
    marginTop: 0,
    marginLeft: 0,
    pitchHorizontal: 50,
    pitchVertical: 25,
    bleed: 0,
    safeArea: 1.5,
  },
  a4CutSheet50x25: {
    key: 'a4CutSheet50x25',
    label: `A4 cut sheet, 50 × 25 mm edge to edge (${
      cutSheetGrid.columns * cutSheetGrid.rows
    } per sheet)`,
    kind: 'cutSheet',
    pageWidth: a4PageWidth,
    pageHeight: a4PageHeight,
    labelWidth: cutSheetLabelWidth,
    labelHeight: cutSheetLabelHeight,
    columns: cutSheetGrid.columns,
    rows: cutSheetGrid.rows,
    marginTop: cutSheetGrid.marginTop,
    marginLeft: cutSheetGrid.marginLeft,
    pitchHorizontal: cutSheetGrid.pitchHorizontal,
    pitchVertical: cutSheetGrid.pitchVertical,
    bleed: 0,
    // A little wider than usual, so a slightly off hand cut never touches the text.
    safeArea: 2,
  },
  a4FortyPerSheet52x29: {
    key: 'a4FortyPerSheet52x29',
    label: 'A4 40 per sheet, 52.5 × 29.7 mm (Avery 3651 size)',
    kind: 'dieCut',
    pageWidth: a4PageWidth,
    pageHeight: a4PageHeight,
    labelWidth: 52.5,
    labelHeight: 29.7,
    columns: 4,
    rows: 10,
    marginTop: 0,
    marginLeft: 0,
    pitchHorizontal: 52.5,
    pitchVertical: 29.7,
    bleed: 0,
    safeArea: 2,
  },
  a4TwentyFourPerSheet70x37: {
    key: 'a4TwentyFourPerSheet70x37',
    label: 'A4 24 per sheet, 70 × 37 mm (Avery 3474 size)',
    kind: 'dieCut',
    pageWidth: a4PageWidth,
    pageHeight: a4PageHeight,
    labelWidth: 70,
    labelHeight: 37,
    columns: 3,
    rows: 8,
    marginTop: 0.5,
    marginLeft: 0,
    pitchHorizontal: 70,
    pitchVertical: 37,
    bleed: 0,
    safeArea: 2,
  },
  a4TwentyOnePerSheet63x38: {
    key: 'a4TwentyOnePerSheet63x38',
    label: 'A4 21 per sheet, 63.5 × 38.1 mm (Avery L7160 size)',
    kind: 'dieCut',
    pageWidth: a4PageWidth,
    pageHeight: a4PageHeight,
    labelWidth: 63.5,
    labelHeight: 38.1,
    columns: 3,
    rows: 7,
    marginTop: 15.15,
    marginLeft: 7.2,
    pitchHorizontal: 66.04,
    pitchVertical: 38.1,
    bleed: 0,
    safeArea: 2,
  },
};

export const defaultStickerPaperPresetKey: StickerPaperPresetKey = 'labelRoll50x25';

export const stickerPaperPresetList = Object.values(stickerPaperPresets);

export const labelsPerSheet = (preset: StickerPaperPreset) => preset.columns * preset.rows;

export const maximumStickerCopies = 500;

// One full sheet, or one label on a roll.
export const defaultStickerCopies = (preset: StickerPaperPreset) =>
  preset.kind === 'roll' ? 1 : labelsPerSheet(preset);

export type StickerPosition = {
  page: number;
  column: number;
  row: number;
  // Trim box corner on its page.
  left: number;
  top: number;
};

// Where each copy goes. On a sheet, copies start at `startPosition` (1 is the top-left label,
// counting along the row), so a part-used sheet can go back in the printer; earlier positions on the
// first page stay blank. A roll prints one label per page and ignores the start position.
export const layoutStickerPositions = ({
  preset,
  copies,
  startPosition,
}: {
  preset: StickerPaperPreset;
  copies: number;
  startPosition: number;
}): StickerPosition[] => {
  const perSheet = labelsPerSheet(preset);
  const copyCount = clampWholeNumber(copies, 1, maximumStickerCopies);
  const firstSlot = preset.kind === 'roll' ? 0 : clampWholeNumber(startPosition, 1, perSheet) - 1;
  return Array.from({ length: copyCount }, (_, index) => {
    const slot = firstSlot + index;
    const cell = slot % perSheet;
    const column = cell % preset.columns;
    const row = Math.floor(cell / preset.columns);
    return {
      page: Math.floor(slot / perSheet),
      column,
      row,
      left: roundMillimetres(preset.marginLeft + column * preset.pitchHorizontal),
      top: roundMillimetres(preset.marginTop + row * preset.pitchVertical),
    };
  });
};

export const clampWholeNumber = (value: number, minimum: number, maximum: number) => {
  const whole = Math.floor(Number.isFinite(value) ? value : minimum);
  return Math.min(maximum, Math.max(minimum, whole));
};

export type CropMark = { left: number; top: number; width: number; height: number };

// Eight hairlines per label, two at each corner, along the trim lines and outside the bleed.
// One page's crop marks: at every vertical cut line, one above the block and one below it.
export const cropMarksForSheet = (preset: StickerPaperPreset): CropMark[] => {
  if (preset.kind !== 'cutSheet') return [];
  const blockTop = preset.marginTop;
  const blockBottom = preset.marginTop + preset.rows * preset.labelHeight;
  return Array.from({ length: preset.columns + 1 }, (_, line) => {
    const x = roundMillimetres(
      preset.marginLeft + line * preset.labelWidth - cropMarkLineWidth / 2,
    );
    return [
      {
        left: x,
        top: roundMillimetres(blockTop - cropMarkGapOutsideTrim - cropMarkLength),
        width: cropMarkLineWidth,
        height: cropMarkLength,
      },
      {
        left: x,
        top: roundMillimetres(blockBottom + cropMarkGapOutsideTrim),
        width: cropMarkLineWidth,
        height: cropMarkLength,
      },
    ];
  }).flat();
};

// One page's cut lines: a hairline along every shared and outer edge of the block, full length,
// so a ruler or guillotine lines up with the whole row or column at once.
export const cutLinesForSheet = (preset: StickerPaperPreset): CropMark[] => {
  if (preset.kind !== 'cutSheet') return [];
  const blockWidth = preset.columns * preset.labelWidth;
  const blockHeight = preset.rows * preset.labelHeight;
  const vertical = Array.from({ length: preset.columns + 1 }, (_, line) => ({
    left: roundMillimetres(preset.marginLeft + line * preset.labelWidth - cutLineWidth / 2),
    top: preset.marginTop,
    width: cutLineWidth,
    height: blockHeight,
  }));
  const horizontal = Array.from({ length: preset.rows + 1 }, (_, line) => ({
    left: preset.marginLeft,
    top: roundMillimetres(preset.marginTop + line * preset.labelHeight - cutLineWidth / 2),
    width: blockWidth,
    height: cutLineWidth,
  }));
  return [...vertical, ...horizontal];
};

// ---- What fits on a label ----

const pointsToMillimetres = 25.4 / 72;
// A rough average character width for Arial, as a share of the font size. Used only to decide what
// to drop; the printed text is also ellipsised by CSS.
const averageCharacterWidthRatio = 0.55;
const minimumBarcodeHeight = 8;
const lineGap = 0.4;

export type StickerDetailPart = 'company' | 'showroom' | 'date';

export type StickerLayout = {
  nameFontSize: number;
  priceFontSize: number;
  detailFontSize: number;
  barcodeHeight: number;
  detailParts: StickerDetailPart[];
  droppedParts: StickerDetailPart[];
};

// The order optional details are given up when a label is too small for them.
export const stickerDetailDropOrder: StickerDetailPart[] = ['date', 'showroom', 'company'];

export const stickerDetailSeparator = ' · ';

// Font sizes (pt) and barcode height (mm) for a label, and which optional details fit on its one
// detail line. The name, the barcode with its SKU and the price always print.
export const fitStickerContent = ({
  labelWidth,
  labelHeight,
  safeArea,
  detailTexts,
}: {
  labelWidth: number;
  labelHeight: number;
  safeArea: number;
  detailTexts: Partial<Record<StickerDetailPart, string>>;
}): StickerLayout => {
  const scale = Math.min(1.4, Math.max(0.7, Math.min(labelWidth / 50, labelHeight / 25)));
  const nameFontSize = roundPoints(8 * scale);
  const priceFontSize = roundPoints(8 * scale);
  const detailFontSize = roundPoints(5.5 * scale);
  const contentWidth = labelWidth - 2 * safeArea;
  const contentHeight = labelHeight - 2 * safeArea;
  const lineHeight = (fontSize: number) => fontSize * pointsToMillimetres * 1.15;

  const chosen = (['company', 'showroom', 'date'] as StickerDetailPart[]).filter(
    (part) => !!detailTexts[part],
  );
  const textWidth = (parts: StickerDetailPart[]) =>
    parts.map((part) => detailTexts[part] ?? '').join(stickerDetailSeparator).length *
    detailFontSize *
    pointsToMillimetres *
    averageCharacterWidthRatio;
  const barcodeHeightWith = (parts: StickerDetailPart[]) =>
    contentHeight -
    lineHeight(nameFontSize) -
    lineHeight(priceFontSize) -
    (parts.length ? lineHeight(detailFontSize) + lineGap : 0) -
    2 * lineGap;

  const isSmallLabel = labelWidth < 50 || labelHeight < 25;
  let kept = [...chosen];
  const dropped: StickerDetailPart[] = [];
  for (const part of stickerDetailDropOrder) {
    const fits =
      kept.length === 0 ||
      (!isSmallLabel &&
        textWidth(kept) <= contentWidth &&
        barcodeHeightWith(kept) >= minimumBarcodeHeight);
    if (fits) break;
    if (kept.includes(part)) {
      kept = kept.filter((keptPart) => keptPart !== part);
      dropped.push(part);
    }
  }

  return {
    nameFontSize,
    priceFontSize,
    detailFontSize,
    barcodeHeight: roundMillimetres(Math.max(minimumBarcodeHeight, barcodeHeightWith(kept))),
    detailParts: kept,
    droppedParts: dropped,
  };
};

const roundPoints = (value: number) => Math.round(value * 10) / 10;

export const stickerDetailPartNames: Record<StickerDetailPart, string> = {
  company: 'Company name',
  showroom: 'Showroom',
  date: 'Date',
};
